import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FileText, Plus, Calendar, DollarSign, CheckCircle, XCircle, 
  Clock, User, Building2, FileSignature, Trash2, AlertTriangle,
  ChevronDown, ChevronUp, Eye, Download, Mail
} from 'lucide-react';
import AmendmentForm from './AmendmentForm';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const AmendmentList = ({ lease, onClose, onSuccess }) => {
  const [amendments, setAmendments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [signing, setSigning] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchAmendments();
    fetchCurrentUser();
  }, [lease.id]);

  const fetchAmendments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/amendments/lease/${lease.id}`, {
        withCredentials: true
      });
      setAmendments(res.data);
    } catch (err) {
      console.error('Error fetching amendments:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const res = await axios.get(`${API_BASE}/auth/me`, { withCredentials: true });
      setCurrentUser(res.data);
    } catch (err) {
      console.error('Error fetching user:', err);
    }
  };

  const handleSignAsStaff = async (amendmentId) => {
    setSigning(true);
    try {
      // Plus d'appel à ipify - le backend récupère l'IP automatiquement
      await axios.post(
        `${API_BASE}/amendments/${amendmentId}/sign/staff`,
        {},
        { withCredentials: true }
      );
      await fetchAmendments();
      alert('✅ Amendment signed successfully as Lessor');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error signing amendment');
    } finally {
      setSigning(false);
    }
  };

  const handleSignAsTenant = async (amendmentId) => {
    setSigning(true);
    try {
      const ownerId = prompt("Enter tenant ID (owner ID):");
      if (!ownerId) return;
      
      // Plus d'appel à ipify - le backend récupère l'IP automatiquement
      await axios.post(
        `${API_BASE}/amendments/${amendmentId}/sign/tenant?owner_id=${ownerId}`,
        {},
        { withCredentials: true }
      );
      await fetchAmendments();
      alert('✅ Amendment signed successfully as Tenant');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error signing amendment');
    } finally {
      setSigning(false);
    }
  };

  const handleSendSignatureEmail = async (amendmentId) => {
    if (!window.confirm('Send signature request email to the tenant?')) return;
    
    setSendingEmail(true);
    try {
      const res = await axios.post(
        `${API_BASE}/amendments/${amendmentId}/send-signature-email`,
        { notify_staff: true },
        { withCredentials: true }
      );
      alert(`✅ Signature email sent to ${res.data.sent_to}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error sending email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDelete = async (amendmentId, permanent = false) => {
    const confirmMsg = permanent 
      ? '⚠️ PERMANENT DELETE: This action cannot be undone. Continue?'
      : 'Soft delete this amendment? (can be restored)';
    
    if (!window.confirm(confirmMsg)) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE}/amendments/${amendmentId}?permanent=${permanent}`, {
        withCredentials: true
      });
      await fetchAmendments();
      alert(permanent ? 'Amendment permanently deleted' : 'Amendment soft deleted');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting amendment');
    } finally {
      setDeleting(false);
    }
  };

  const getAmendmentTypeStyle = (type) => {
    return type === 'amendment' 
      ? 'bg-yellow-500/20 text-yellow-400'
      : 'bg-purple-500/20 text-purple-400';
  };

  const getSignatureStatus = (amendment) => {
    const tenantSigned = amendment.signed_by_tenant_at;
    const staffSigned = amendment.signed_by_staff_at;
    
    if (tenantSigned && staffSigned) {
      return { label: 'Fully Signed', color: 'text-green-400', icon: CheckCircle };
    }
    if (tenantSigned) {
      return { label: 'Tenant Signed', color: 'text-blue-400', icon: User };
    }
    if (staffSigned) {
      return { label: 'Staff Signed', color: 'text-orange-400', icon: Building2 };
    }
    return { label: 'Pending', color: 'text-red-400', icon: Clock };
  };

  const userCanDelete = () => {
    return currentUser?.permissions?.level >= 100;
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="p-5 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-white font-bold text-lg flex items-center gap-2">
                <FileSignature size={20} className="text-blue-400" />
                Amendments & Addendums
              </h3>
              <p className="text-gray-400 text-sm mt-1">
                Lease {lease.lease_number} — {lease.hangar_number} · {lease.tenant_name}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowForm(true)}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm flex items-center gap-1"
              >
                <Plus size={14} /> Add Amendment
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white"
              >
                <XCircle size={24} />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
            </div>
          ) : amendments.length === 0 ? (
            <div className="text-center py-10">
              <FileSignature size={48} className="text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500">No amendments or addendums for this lease.</p>
              <button
                onClick={() => setShowForm(true)}
                className="mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm"
              >
                Create First Amendment
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {amendments.map((amendment) => {
                const isExpanded = expandedId === amendment.id;
                const signatureStatus = getSignatureStatus(amendment);
                const SignatureIcon = signatureStatus.icon;
                
                return (
                  <div key={amendment.id} className="bg-gray-700/30 rounded-lg border border-gray-600 overflow-hidden">
                    {/* Header row */}
                    <div 
                      className="p-4 cursor-pointer hover:bg-gray-700/50 transition"
                      onClick={() => setExpandedId(isExpanded ? null : amendment.id)}
                    >
                      <div className="flex flex-wrap justify-between items-center gap-2">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded-lg text-xs font-medium ${getAmendmentTypeStyle(amendment.amendment_type)}`}>
                            #{amendment.amendment_number} • {amendment.amendment_type.toUpperCase()}
                          </span>
                          <span className="text-white font-medium">{amendment.title}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className={`flex items-center gap-1 text-xs ${signatureStatus.color}`}>
                            <SignatureIcon size={12} />
                            <span>{signatureStatus.label}</span>
                          </div>
                          <span className="text-gray-500 text-xs">
                            {new Date(amendment.effective_date).toLocaleDateString()}
                          </span>
                          {isExpanded ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
                        </div>
                      </div>
                    </div>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div className="border-t border-gray-600 p-4 space-y-4">
                        {/* Description */}
                        {amendment.description && (
                          <div>
                            <p className="text-gray-400 text-xs mb-1">Description</p>
                            <p className="text-gray-300 text-sm">{amendment.description}</p>
                          </div>
                        )}

                        {/* Changes */}
                        {amendment.changes && Object.keys(amendment.changes).length > 0 && (
                          <div>
                            <p className="text-gray-400 text-xs mb-1">Changes</p>
                            <div className="bg-gray-800 rounded-lg p-3">
                              {Object.entries(amendment.changes).map(([key, value]) => (
                                <div key={key} className="flex justify-between text-sm">
                                  <span className="text-gray-400">{key}:</span>
                                  <span className="text-white font-mono">
                                    {typeof value === 'object' ? JSON.stringify(value) : value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Email button for pending amendments */}
                        {!amendment.signed_by_tenant_at && (
                          <div className="flex justify-end">
                            <button
                              onClick={() => handleSendSignatureEmail(amendment.id)}
                              disabled={sendingEmail}
                              className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm flex items-center gap-1"
                            >
                              <Mail size={14} />
                              {sendingEmail ? 'Sending...' : 'Send Signature Email to Tenant'}
                            </button>
                          </div>
                        )}

                        {/* Signatures */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-gray-800/50 rounded-lg p-3">
                            <p className="text-gray-400 text-xs mb-2 flex items-center gap-1">
                              <User size={12} /> Tenant Signature
                            </p>
                            {amendment.signed_by_tenant_at ? (
                              <div>
                                <p className="text-green-400 text-sm flex items-center gap-1">
                                  <CheckCircle size={14} /> Signed
                                </p>
                                <p className="text-gray-500 text-xs">
                                  {new Date(amendment.signed_by_tenant_at).toLocaleString()}
                                </p>
                                <p className="text-gray-500 text-xs">IP: {amendment.ip_address_tenant}</p>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleSignAsTenant(amendment.id)}
                                disabled={signing}
                                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm"
                              >
                                Sign as Tenant
                              </button>
                            )}
                          </div>

                          <div className="bg-gray-800/50 rounded-lg p-3">
                            <p className="text-gray-400 text-xs mb-2 flex items-center gap-1">
                              <Building2 size={12} /> Lessor Signature
                            </p>
                            {amendment.signed_by_staff_at ? (
                              <div>
                                <p className="text-green-400 text-sm flex items-center gap-1">
                                  <CheckCircle size={14} /> Signed
                                </p>
                                <p className="text-gray-500 text-xs">
                                  {new Date(amendment.signed_by_staff_at).toLocaleString()}
                                </p>
                                <p className="text-gray-500 text-xs">IP: {amendment.ip_address_staff}</p>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleSignAsStaff(amendment.id)}
                                disabled={signing}
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm"
                              >
                                Sign as Lessor
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Document */}
                        {amendment.document_url && (
                          <div>
                            <a
                              href={`${API_BASE}${amendment.document_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 text-sm flex items-center gap-1 hover:underline"
                            >
                              <FileText size={12} /> {amendment.document_filename || 'View Document'}
                            </a>
                          </div>
                        )}

                        {/* Delete button (only for level 100+) */}
                        {userCanDelete() && (
                          <div className="flex justify-end gap-2 pt-2 border-t border-gray-700">
                            <button
                              onClick={() => handleDelete(amendment.id, false)}
                              className="px-3 py-1.5 bg-gray-600 hover:bg-gray-500 rounded-lg text-white text-xs"
                            >
                              Soft Delete
                            </button>
                            <button
                              onClick={() => handleDelete(amendment.id, true)}
                              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-white text-xs flex items-center gap-1"
                            >
                              <Trash2 size={12} /> Permanent Delete
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Amendment Form Modal */}
      {showForm && (
        <AmendmentForm
          lease={lease}
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false);
            fetchAmendments();
            if (onSuccess) onSuccess();
          }}
        />
      )}
    </div>
  );
};

export default AmendmentList;